import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { init, pageview, identify } from './tracker';

/**
 * AnalyticsProvider — mounts once inside <Router> so it has access to useLocation.
 * Emits a pageview on every route change, identifies user when auth state changes.
 * StrictMode-safe: uses a ref to guard against double-emitting the same path.
 */
export default function AnalyticsProvider({ children }) {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const lastPathRef = useRef(null);
  const trackerInitialized = useRef(false);

  // Initialize tracker once
  useEffect(() => {
    if (!trackerInitialized.current) {
      init();
      trackerInitialized.current = true;
    }
  }, []);

  // Emit pageview on route change — StrictMode-safe ref guard.
  // Use pathname only (strip querystring/hash) so analytics groups don't get
  // fragmented by query params like ?tab=realtime.
  useEffect(() => {
    const path = location.pathname;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    pageview(path, document.title);
  }, [location]);

  // Identify user when auth state changes
  useEffect(() => {
    if (user) {
      identify(user.id, user.role);
    } else {
      identify(null, null);
    }
  }, [user]);

  return <>{children}</>;
}

AnalyticsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
